import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './Dashboard.module.scss';
import Loader from '../../components/Loader/Loader';
import {
  getApplicationDataIsLoading,
  getApplicationDataHasFailed,
} from '../../redux/modules/applicationData/applicationData.selectors';
import { initDashboardServiceCalls } from '../../services/index';
import { clearShoppingCart } from '../../redux/modules/shoppingCart/shoppingCart.module';
import Header from '../../components/DashboardLayout/Header/Header';
import Section from '../../components/DashboardLayout/Section/Section';
import OtherOptions from '../../components/DashboardLayout/OtherOptions/OtherOptions';
import RMDCalculatorEntry from './RMDCalculatorEntry';
import RemainingRMD from '../../components/DashboardLayout/RemainingRMD/RemainingRMD';
import FAQ from '../../components/DashboardLayout/FAQ/FAQ';
import RMDFooter from '../../components/DashboardLayout/RMDFooter/RMDFooter';
import RMDAccountsNew from '../../components/DashboardLayout/RMDAccounts/RMDAccountsNew';
import { getOnlyOWNRRMDs } from '../../redux/modules/UserFunds/userFunds.selectors';
import RecentTransactionsTable from '../../components/DashboardLayout/RecentTransactionsTable/RecentTransactionsTable';
import RMDEnabled from '../../components/DashboardLayout/RMDEnabled/RMDEnabled';
import SellEnabled from '../../components/DashboardLayout/RMDEnabled/SellEnabled';
import ExchangeEnabled from '../../components/DashboardLayout/RMDEnabled/ExchangeEnabled';
import { getAllSystematics } from '../../redux/modules/existingAutomatics/existingAutomatics.selectors';
import {
  getRMDSummary,
  getEligibleForRMD,
  hasRothConverseionOnRmdAgreements,
  getShowDashboard,
  getDashboardStatus,
} from '../../redux/modules/RMD/RMDSetting.selectors';
import ErrorPage from '../landing/ErrorPage';
import { getRecentTransactionList } from '../../redux/modules/recentTransactions/recentTransactions.selectors';
import RMDNotifyBanner from '../../components/RMDNotifyBanner/RMDNotifyBanner';
import Button from '../../components/Button/Button';
import errorStyles from '../landing/ErrorPage.module.scss';
import config from '../../config/config';
import RMDCalculatorPreRMD from '../../components/DashboardLayout/RemainingRMD/RMDCalculatorPreRMD';
import {getAllQCDHistory} from "../../redux/modules/qcdHistory/QCDHistory.selector";
import { getSalesforceRep } from '../../redux/modules/applicationConfig/applicationConfig.selectors';
import { isCustomerWebViewEnabled } from '../../redux/modules/customer/customer.selectors';
import CTATiles from './CTATiles';
import warningSvg from '../../assets/images/warning-exclamation.svg';

const DashboardUnavailable = ({ dashboardStatus }) => {
  return (
    <div className={styles['dashboard-unavailable-container']}>
      <img src={warningSvg} />
      <div>
        <h1>Required Minimum Distribution (RMD) information is temporarily unavailable.</h1>
      </div>
      <div className={styles['dashboard-unavailable-status-text']}>
        {dashboardStatus}
      </div>
      <a
        className={styles['dashboard-unavailable-return-link']} 
        href={`${config.url}/usis/my-accounts/dashboard`}>Return to Dashboard</a>
    </div>
  )
}

export const PreRMDEligibleSection = ({qcdSumaries = []}) => (
  <div
    className={`pre-rmd-eligible-section ${styles['pre-rmd-eligible-section']}`}
  >
    <Section>
      <div className={styles['title']}>
        Our records indicate that you are <br />
        approaching RMD age.
      </div>
      <div className={styles['description']}>
        <p>
          Beginning the year you turn 73, the IRS requires that you start taking
          mandatory withdrawals from your Traditional IRA, Rollover IRA,
          SEP-IRA, or SIMPLE IRA. This annual withdrawal is known as a Required
          Minimum Distribution (RMD). Roth IRAs do not require withdrawals until
          after the death of the original owner.
        </p>
        <p>
          <RMDCalculatorPreRMD
            trigger={
              <span className={styles['description-link']}>Your RMD </span>
            }
          />{' '}
          for a year is determined by dividing the previous year-end's fair
          market value (on December 31) by the applicable distribution period
          (from the appropriate life expectancy table). The year-end value may
          need to be adjusted for certain events. Your RMD changes each year,
          based on your balance and age. If you don’t take the correct RMD
          amount, IRS penalties may apply.
        </p>
      </div>
      <div className={styles.readMore}>
        <a
          href="/personal-investing/resources/planning/rmds/index.html"
          rel="noopener noreferrer"
          target="_blank"
        >
          <Button type="primary" size="auto">
            Read More About RMDs
          </Button>
        </a>
      </div>
      <div>
        <div className={styles['secondary-title']}>
          Consider these steps to prepare for your RMD.
        </div>
        <div className={styles['description']}>
          Even before you're required to take an RMD, you can set up your
          account profile so you’re ready to make the choices that are right for
          you regarding your Required Minimum Distributions.
        </div>

        <RMDCalculatorEntry />

        <CTATiles preRMD={true} qcdSumaries={qcdSumaries} />
      </div>
    </Section>
  </div>
);
const Dashboard = (props) => {
  const qcdHistory = useSelector(getAllQCDHistory);
  const isLoading = useSelector(getApplicationDataIsLoading);
  const hasRMD = useSelector(getOnlyOWNRRMDs);
  const rmdSummaryInfo = useSelector(getRMDSummary);
  const isEligible = useSelector(getEligibleForRMD);
  const showYTDColumns = rmdSummaryInfo.displayDistributionColumns;
  const isSalesforceRep = useSelector((state) => getSalesforceRep(state));
  const { preRMDEligible } = rmdSummaryInfo;
  const showIRASection = useSelector(hasRothConverseionOnRmdAgreements);
  const { autoExchangePlusAccounts, autoSellPlusAccounts } =
    useSelector(getAllSystematics);
  const recentTransactions = useSelector(getRecentTransactionList);
  const dispatch = useDispatch();
  const customerWebViewEnabled = useSelector(isCustomerWebViewEnabled);
  const hasError = useSelector(getApplicationDataHasFailed);
  const showDashboard = useSelector(getShowDashboard);
  const dashboardStatus = useSelector(getDashboardStatus);
  let showFaq = true;
  const callingRMDDetails = false;

  useEffect(() => {
    initDashboardServiceCalls(dispatch);
    dispatch(clearShoppingCart());
  }, [dispatch]);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Loader isVisible={true} />
      </div>
    );
  }

  if (hasError) {
    return <ErrorPage />;
  }

  if (
    !isEligible &&
    !preRMDEligible &&
    !isSalesforceRep &&
    !customerWebViewEnabled
  ) {
    return (
      <ErrorPage
        errorMsg={null}
        errorHeading={{
          msg: `Unfortunately, we’re not able to manage your Required Minimum Distribution (RMD) online.`,
          style: 'title',
        }}
        errorRedirect={{
          link: 'usis/my-accounts/dashboard',
          text: 'Return to Dashboard',
        }}
        errorPageBody={
          <div className={errorStyles['rmd-error-body']}>
            <p>This may be because: </p>
            <ul className={errorStyles['bulltes-points-rmd-errors']}>
              <li>
                You're under the age for RMDs (minimum 73 years as of December
                31st of this year)
              </li>
              <li>
                You didn’t have an account, as of December 31 of last year, that
                required an RMD, OR
              </li>
              <li>
                We’re not currently able to support this experience for the
                following accounts:
                <br />
                Workplace Retirement or Inherited IRA.
              </li>
            </ul>
            <p className={errorStyles['bulltes-points-rmd-errors']}>
              To learn more about RMDs, please visit{' '}
              <a
                className={errorStyles['error-link']}
                rel="noopener noreferrer"
                href={`${config.url}/personal-investing/resources/planning/rmds/index.html`}
                target="_blank"
              >
                troweprice.com/rmd
              </a>
            </p>
            <p className={errorStyles['mr-top']}>
              As always, please{' '}
              <a
                className={'error-link'}
                rel="noopener noreferrer"
                href={`${config.url}/personal-investing/help/contact/index.html`}
                target="_blank"
              >
                contact us
              </a>{' '}
              with any questions
            </p>
          </div>
        }
      />
    );
  }

  if (!showDashboard) {
    return <DashboardUnavailable dashboardStatus={dashboardStatus} />;
  }

  const isPostLaunch = (dateAndTime) => { 
    const currentDate = new Date();
    const launchDate = new Date(dateAndTime); 
    return currentDate > launchDate; 
  };

  return (
    <div className="dashboard-wrapper">
      <div className={styles['background']}>
        <div className={styles['container']}>
          {/* Header and Print Section*/}
          <Header />
          {(preRMDEligible ||
            ((isSalesforceRep || customerWebViewEnabled) && !isEligible)) && (
            <PreRMDEligibleSection
              dispatch={dispatch}
              qcdSumaries={qcdHistory}
            />
          )}
          {!preRMDEligible && isEligible && (
            <Section>
              <RemainingRMD />
            </Section>
          )}
          {!preRMDEligible && isEligible && hasRMD.length !== 0 && (
            <Section>
              <RMDEnabled
                rmdInfo={hasRMD}
                callingRMDDetails={callingRMDDetails}
              />
            </Section>
          )}
          {!preRMDEligible && isEligible && autoSellPlusAccounts.length > 0 && (
            <Section>
              <SellEnabled />
            </Section>
          )}
          {!preRMDEligible &&
            isEligible &&
            autoExchangePlusAccounts.length > 0 && (
              <Section>
                <ExchangeEnabled />
              </Section>
            )}
          {!preRMDEligible && isEligible && recentTransactions?.length > 0 && (
            <Section>
              <RecentTransactionsTable />
            </Section>
          )}
          {!preRMDEligible && (
            <Section>
              <OtherOptions />
            </Section>
          )}
          {(isEligible || preRMDEligible) && showIRASection && (
            <Section>
              <RMDAccountsNew />
            </Section>
          )}
          {!preRMDEligible && (
            <Section>
              <RMDCalculatorEntry preRMDEligible={preRMDEligible} />
            </Section>
          )}

          <Section>
            <FAQ showFaq={showFaq} isPreRMDEligible={preRMDEligible} isPostLaunch={isPostLaunch} />
          </Section>
          <Section>
            <RMDFooter isPostLaunch={isPostLaunch} showYTDColumns={showYTDColumns} />
          </Section>
          <div style={{ position: 'relative', bottom: '10px' }}>3292270</div>
        </div>
      </div>
      <style>
        {`
           .pre-rmd-eligible-section button {
            height: auto;
          }
      `}
      </style>
    </div>
  );
};

export default Dashboard;
